import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useLanguage } from "../i18n";

const PORT_BG =
  "https://images.unsplash.com/photo-1613690399151-65ea69478674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBwb3J0JTIwY29udGFpbmVyJTIwdHJhZGV8ZW58MHx8fHwxNzg5NTA0MjE5fDA&ixlib=rb-4.1.0&q=85";

const TESTIDS = ["stat-counter-countries", "stat-counter-volume", "stat-counter-sustainability", "stat-counter-monitoring"];

const Counter = ({ value, lang }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) =>
        setDisplay(
          Math.round(v).toLocaleString(lang === "tr" ? "tr-TR" : lang === "ru" ? "ru-RU" : "en-US")
        ),
    });
    return () => controls.stop();
  }, [inView, value, lang]);

  return <span ref={ref}>{display}</span>;
};

export const WhyUsStats = () => {
  const { t, lang } = useLanguage();

  return (
    <section data-testid="stats-section" className="relative overflow-hidden bg-[#07170f] py-24 sm:py-32">
      <div className="absolute inset-0">
        <img src={PORT_BG} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07170f] via-transparent to-[#07170f]" />
        <div className="absolute left-1/2 top-0 h-72 w-[42rem] -translate-x-1/2 rounded-full bg-[#48e38e]/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#48e38e]"
          >
            {t.stats.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl"
            data-testid="stats-title"
          >
            {t.stats.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60"
          >
            {t.stats.sub}
          </motion.p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
          {t.stats.items.map((item, i) => (
            <motion.div
              key={item.label}
              data-testid={TESTIDS[i]}
              data-cursor
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.08 * i, ease: [0.16, 1, 0.3, 1] }}
              className={`gradient-border group flex flex-col justify-center rounded-2xl border border-white/10 bg-white/[0.04] p-7 text-center backdrop-blur-sm transition-all duration-500 hover:-translate-y-1.5 hover:border-transparent hover:bg-white/[0.07] hover:shadow-[0_24px_60px_-20px_rgba(72,227,142,0.35),inset_0_0_50px_rgba(72,227,142,0.06)] ${["lg:col-span-2", "sm:col-span-2 lg:col-span-4", "lg:col-span-3", "lg:col-span-3"][i]}`}
            >
              <div className={`font-display font-extrabold tracking-tight text-white transition-transform duration-500 group-hover:scale-105 ${i === 1 ? "text-5xl sm:text-6xl" : "text-4xl sm:text-5xl"}`}>
                {item.pre[lang] ?? item.pre.en}
                <span className="text-[#48e38e]">
                  <Counter value={item.value} lang={lang} />
                </span>
                {item.suf[lang] ?? item.suf.en}
              </div>
              <div className="mx-auto mt-4 h-px w-2/3 bg-gradient-to-r from-transparent via-[#48e38e]/60 to-transparent" />
              <h3 className="font-display mt-4 text-base font-semibold text-white">{item.label}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
